import { cn } from '@e-kyc/utils';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { useCallback, useMemo, useState, type HTMLAttributes } from 'react';
import { useController, type FieldValues, type UseControllerProps } from 'react-hook-form';

import IconButton from '../../icon-button';
import { CloseRoundedIcon, ExpandMoreOutlinedIcon } from '../../icons';

import { MenuItem } from './menu-item';

export interface FormSelectOption {
  value: string;
  label: string;
}

export type SelectProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  React.ComponentProps<'input'> & {
    label?: string;
    options: FormSelectOption[];
    required?: boolean;
    placeholder?: string;
    allowClear?: boolean;
    helperText?: string;
    disableInput?: boolean;
    renderDisplayValue?: (value: FormSelectOption['value']) => string;
    classNameOverrides?: {
      optionWrapper?: HTMLAttributes<'div'>['className'];
    };
  };

export function Select<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<SelectProps<TFieldValues>>,
) {
  const {
    id,
    label,
    options = [],
    placeholder = '',
    name: nameProp,
    required,
    disabled: isDisabled = false,
    className,
    allowClear = false,
    helperText,
    disableInput = false,
    renderDisplayValue,
    classNameOverrides = {},
    ...restProps
  } = props;
  const { optionWrapper: optionWrapperClassName } = classNameOverrides;

  const [searchInput, setSearchInput] = useState('');

  const {
    field: { onChange, name, value, disabled },
    fieldState: { error },
  } = useController({
    ...restProps,
    name: nameProp,
    disabled: isDisabled,
    rules: { required, ...restProps.rules },
  });
  const isError = restProps['aria-invalid'] === 'true';

  const clearButtonVisible = allowClear && (searchInput || value);

  const filteredOptions = useMemo(() => {
    if (options.length === 0) {
      return [];
    }
    if (!searchInput) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchInput.toLowerCase()),
    );
  }, [options, searchInput]);

  const defaultRenderDisplayValue = useCallback(
    (item: FormSelectOption['value']) => {
      const selectedOption = options.find((option) => option.value === item);
      return selectedOption ? selectedOption.label : '';
    },
    [options],
  );

  const handleSearch = (text: string) => {
    setSearchInput(text);
  };

  const handleClear = () => {
    setSearchInput('');
    onChange('');
  };

  return (
    <div id={id}>
      <label htmlFor={name}>
        {label ? (
          <p
            className={cn(
              'mb-2 text-start text-sm font-semibold',
              required && 'after:text-danger after:ml-1 after:content-["*"]',
            )}
          >
            {label}
          </p>
        ) : null}
        <Combobox immediate name={name} value={value} onChange={onChange} disabled={disabled}>
          {({ open }) => (
            <>
              <div className='relative'>
                <ComboboxInput
                  displayValue={renderDisplayValue ?? defaultRenderDisplayValue}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder={placeholder}
                  className={cn(
                    'focus:border-primary focus:shadow-as-border w-full rounded-md border-2 border-[#0000001f] py-2.5 pl-3.5 outline-none',
                    clearButtonVisible ? 'pr-18' : 'pr-10',
                    disabled && 'cursor-not-allowed bg-gray-100 text-gray-400',
                    (error || isError) &&
                      'border-danger focus:border-danger focus:shadow-as-border-danger',
                    className,
                  )}
                  disabled={disabled || disableInput}
                  inputMode={restProps.inputMode}
                />
                <div
                  className={cn(
                    'absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3.5 text-gray-500',
                    disabled && 'cursor-not-allowed',
                  )}
                >
                  {clearButtonVisible ? (
                    <IconButton className='rounded-full p-1.5' onClick={handleClear}>
                      <CloseRoundedIcon />
                    </IconButton>
                  ) : null}
                  <ComboboxButton
                    className={cn('cursor-pointer', disabled && 'cursor-not-allowed')}
                  >
                    <ExpandMoreOutlinedIcon
                      className={cn(
                        'transition-transform duration-200 ease-in-out',
                        open ? 'rotate-180 transform' : '',
                      )}
                    />
                  </ComboboxButton>
                </div>
              </div>
              <ComboboxOptions
                anchor='bottom'
                transition
                className={cn(
                  'w-(--input-width) rounded-lg border border-gray-200 bg-white py-1 [--anchor-gap:--spacing(1)] empty:invisible',
                  'transition duration-100 ease-in data-leave:data-closed:opacity-0',
                  optionWrapperClassName,
                )}
              >
                {filteredOptions.map((option) => (
                  <ComboboxOption
                    key={option.value}
                    value={option.value}
                    className='cursor-pointer px-1.5 py-0.5'
                  >
                    {({ selected }) => <MenuItem selected={selected}>{option.label}</MenuItem>}
                  </ComboboxOption>
                ))}
              </ComboboxOptions>
            </>
          )}
        </Combobox>
      </label>
      {/* `thai-wrap`: see the note in global.css — stops iOS Safari breaking mid-word
          inside a Thai run. */}
      {error || helperText ? (
        <p className={cn('text-text-secondary thai-wrap mt-2 text-xs', error && 'text-danger')}>
          {error?.message ?? helperText}
        </p>
      ) : null}
    </div>
  );
}
